// uploadQueue — IndexedDB-backed FIFO queue for background media uploads.
//
// Why this exists (F-wave β / F2):
//   The SelfieSheet KEEP flow used to await nightApi.uploadMedia() before
//   closing — the sheet stayed open for the full filter + upload roundtrip
//   (~3-8s on a 4G connection at a loud bar). User feedback was unambiguous:
//   "the sheet feels stuck after I tap keep". The fix is to make KEEP close
//   the sheet in <100ms and queue the upload to run in the background.
//
// Two persistence layers:
//   1. IndexedDB store `pending_uploads` — survives tab close, mid-upload
//      page reload, killed-by-iOS Safari memory pressure. drainQueue() runs
//      once on app boot to retry anything that didn't make it.
//   2. In-memory ring fallback — for browsers / sessions where IDB is
//      unavailable (private browsing, quota exceeded, ITP restrictions).
//      Same FIFO semantics; no persistence across reloads but the user keeps
//      moving and the data eventually uploads.
//
// Events (window-scoped CustomEvent, listened to by SelfieSheet + index route):
//   - 'stumble:media-saved'   — upload succeeded; detail = { nightId, mediaId, mediaUrl? }
//   - 'stumble:media-failed'  — upload failed after retry exhaustion; detail = { nightId, message }
//   - 'stumble:storage-full'  — IDB write rejected; detail = { reason }
//
// Retry policy:
//   Up to 3 attempts per item with exponential backoff (1s, 4s, 9s — quadratic
//   so we don't hammer a flaking connection). After exhaustion the item is
//   removed from the store and a 'stumble:media-failed' event fires so the
//   UI can offer manual retry via the existing SelfieSheet path.

import * as Sentry from '@sentry/browser'
import { type MediaDto, nightApi } from '../../api'

// G1.4: breadcrumb wrapper for the upload queue. The queue is the most likely
// culprit for "selfie stuck" — a silent IDB write failure or a stuck drain
// loop. Every queue operation drops a breadcrumb so the next time a user
// reports stuck media we can see the queue's trail. Wrapped in try/catch so
// Sentry can never break uploads.
function breadcrumb(message: string, data?: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({
      category: 'upload-queue',
      message,
      level: 'info',
      data,
    })
  } catch {
    // swallow — observability must not break uploads
  }
}

const DB_NAME = 'stumble-uploads'
const DB_VERSION = 1
const STORE_NAME = 'pending_uploads'
const MAX_ATTEMPTS = 3
// Backoff schedule in ms — attempt 1: 1000, attempt 2: 4000, attempt 3: 9000.
const BACKOFF_MS = (attempt: number): number => attempt * attempt * 1000

export const MEDIA_SAVED_EVENT = 'stumble:media-saved'
export const MEDIA_FAILED_EVENT = 'stumble:media-failed'
export const STORAGE_FULL_EVENT = 'stumble:storage-full'

export type MediaSavedEventDetail = {
  nightId: string
  mediaId: string
  mediaUrl: string | null
}

export type MediaFailedEventDetail = {
  nightId: string
  message: string
}

export type StorageFullEventDetail = {
  reason: string
}

export type UploadKind = 'selfie' | 'drink-proof'

type QueueRecord = {
  id: string
  nightId: string
  kind: UploadKind
  blob: Blob
  mimeType: string
  attemptCount: number
  queuedAt: number
}

// In-memory fallback queue — keyed by id, ordered by queuedAt.
const memoryQueue = new Map<string, QueueRecord>()

let processing = false

// ── IndexedDB helpers ───────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  })
}

async function idbPut(record: QueueRecord): Promise<boolean> {
  let db: IDBDatabase | null = null
  try {
    db = await openDb()
  } catch {
    breadcrumb('idb-open-threw')
    return false
  }
  if (!db) return false
  return new Promise((resolve) => {
    // biome-ignore lint/style/noNonNullAssertion: guarded by `if (!db)` above
    const handle = db!
    try {
      const tx = handle.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      // The put() itself can throw synchronously for some quota errors before
      // the transaction even reports onerror — catch it here too.
      try {
        store.put(record)
      } catch (err) {
        breadcrumb('idb-put-threw-sync', { reason: errMessage(err) })
        try {
          handle.close()
        } catch {
          /* noop */
        }
        resolve(false)
        return
      }
      tx.oncomplete = () => {
        handle.close()
        resolve(true)
      }
      tx.onerror = () => {
        breadcrumb('idb-tx-error', { reason: errMessage(tx.error) })
        handle.close()
        resolve(false)
      }
      tx.onabort = () => {
        breadcrumb('idb-tx-abort', { reason: errMessage(tx.error) })
        handle.close()
        resolve(false)
      }
    } catch (err) {
      breadcrumb('idb-tx-open-threw', { reason: errMessage(err) })
      try {
        handle.close()
      } catch {
        /* noop */
      }
      resolve(false)
    }
  })
}

function errMessage(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`
  if (err && typeof err === 'object' && 'name' in err)
    return String((err as { name: unknown }).name)
  return 'unknown'
}

async function idbDelete(id: string): Promise<void> {
  const db = await openDb()
  if (!db) return
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).delete(id)
      tx.oncomplete = () => {
        db.close()
        resolve()
      }
      tx.onerror = () => {
        db.close()
        resolve()
      }
    } catch {
      db.close()
      resolve()
    }
  })
}

async function idbGetAll(): Promise<QueueRecord[]> {
  const db = await openDb()
  if (!db) return []
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).getAll()
      req.onsuccess = () => {
        db.close()
        const items = (req.result ?? []) as QueueRecord[]
        // FIFO order — oldest queuedAt first.
        items.sort((a, b) => a.queuedAt - b.queuedAt)
        resolve(items)
      }
      req.onerror = () => {
        db.close()
        resolve([])
      }
    } catch {
      db.close()
      resolve([])
    }
  })
}

async function idbIncrementAttempt(id: string, attemptCount: number): Promise<void> {
  const db = await openDb()
  if (!db) return
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const getReq = store.get(id)
      getReq.onsuccess = () => {
        const existing = getReq.result as QueueRecord | undefined
        if (existing) {
          existing.attemptCount = attemptCount
          store.put(existing)
        }
      }
      tx.oncomplete = () => {
        db.close()
        resolve()
      }
      tx.onerror = () => {
        db.close()
        resolve()
      }
    } catch {
      db.close()
      resolve()
    }
  })
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Enqueue a blob for background upload. Writes to IndexedDB if available, falls
 * back to an in-memory queue otherwise. Kicks the processor — the actual
 * POST happens out-of-band; the caller does NOT await it.
 */
export async function queueUpload(blob: Blob, nightId: string, kind: UploadKind): Promise<void> {
  const record: QueueRecord = {
    id: cryptoRandomUUID(),
    nightId,
    kind,
    blob,
    mimeType: blob.type || 'image/webp',
    attemptCount: 0,
    queuedAt: Date.now(),
  }
  breadcrumb('enqueue', { id: record.id, kind, sizeKb: Math.round(blob.size / 1024) })
  let persisted = false
  try {
    persisted = await idbPut(record)
  } catch (err) {
    // idbPut resolves rather than rejects, but defence in depth — never let a
    // future regression drop an upload silently.
    breadcrumb('idb-put-await-threw', { reason: errMessage(err) })
  }
  if (!persisted) {
    // IDB unavailable / quota full / private mode — fall back to memory and
    // notify the UI so it can show a soft warning. We DO NOT drop the upload:
    // the memory queue still processes; only persistence-across-reload is lost.
    breadcrumb('enqueue-fallback-memory', { id: record.id, memorySize: memoryQueue.size + 1 })
    memoryQueue.set(record.id, record)
    dispatch(STORAGE_FULL_EVENT, { reason: 'indexeddb-unavailable' })
  }
  // Trigger the drain — runs async, does not block the caller.
  void processQueue()
}

/**
 * Drain any pending uploads — both IDB-persisted (from a prior session) and
 * any in-memory items. Call once on app boot. Safe to call repeatedly; the
 * `processing` guard collapses concurrent invocations.
 */
export async function drainQueue(): Promise<void> {
  await processQueue()
}

async function processQueue(): Promise<void> {
  if (processing) {
    breadcrumb('drain-skip-already-processing')
    return
  }
  processing = true
  breadcrumb('drain-start')
  let drained = 0
  try {
    // Walk one at a time. IDB items are processed first (older), then memory.
    while (true) {
      const persisted = await idbGetAll()
      const next = persisted[0] ?? firstMemoryItem()
      if (!next) break
      await processOne(next)
      drained += 1
    }
  } finally {
    processing = false
    breadcrumb('drain-end', { drained })
  }
}

function firstMemoryItem(): QueueRecord | undefined {
  if (memoryQueue.size === 0) return undefined
  let oldest: QueueRecord | undefined
  for (const r of memoryQueue.values()) {
    if (!oldest || r.queuedAt < oldest.queuedAt) oldest = r
  }
  return oldest
}

async function processOne(record: QueueRecord): Promise<void> {
  const attempt = record.attemptCount + 1
  breadcrumb('process-start', { id: record.id, attempt, kind: record.kind })
  if (attempt > 1) {
    // Backoff before retry. We don't backoff before the first attempt — that's
    // the user's expectation ("I tapped keep, upload now").
    await sleep(BACKOFF_MS(attempt - 1))
  }
  try {
    let media: MediaDto
    if (record.kind === 'selfie') {
      const res = await nightApi.uploadMedia(record.nightId, record.blob, {
        filterPreset: 'stumble-cam-v1',
      })
      media = res.media
    } else {
      const res = await nightApi.uploadMedia(record.nightId, record.blob)
      media = res.media
    }
    // Success — remove from both stores and notify.
    breadcrumb('process-success', { id: record.id, mediaId: media.id })
    await removeRecord(record.id)
    dispatch(MEDIA_SAVED_EVENT, {
      nightId: record.nightId,
      mediaId: media.id,
      mediaUrl: media.publicUrl ?? media.dataUrl ?? null,
    })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'upload failed'
    if (attempt >= MAX_ATTEMPTS) {
      // Give up. Remove the record so we don't loop forever on a poison item;
      // surface the failure so the UI can offer manual retake/retry.
      breadcrumb('process-give-up', { id: record.id, attempt, reason: message })
      await removeRecord(record.id)
      dispatch(MEDIA_FAILED_EVENT, { nightId: record.nightId, message })
      return
    }
    breadcrumb('process-retry', { id: record.id, attempt, reason: message })
    // Bump the attempt count in both stores so retry-on-reload survives.
    record.attemptCount = attempt
    if (memoryQueue.has(record.id)) memoryQueue.set(record.id, record)
    await idbIncrementAttempt(record.id, attempt)
    // Loop continues — next iteration will pick the same item back up (or a
    // newer one if it cuts ahead via queuedAt sort, which it won't because
    // queuedAt is monotone — but the guard is there).
  }
}

async function removeRecord(id: string): Promise<void> {
  memoryQueue.delete(id)
  await idbDelete(id)
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

function dispatch<T>(name: string, detail: T): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(name, { detail }))
}

// crypto.randomUUID is widely available in modern browsers + happy-dom. The
// `globalThis as any` cast keeps the file usable in test envs where crypto
// may not exist on globalThis directly.
function cryptoRandomUUID(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } }
  if (g.crypto?.randomUUID) return g.crypto.randomUUID()
  // Fallback — RFC4122-ish, not cryptographically strong, only used in test envs.
  return `upload-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

// ── Test-only exports ──────────────────────────────────────────────────────
// Vitest reaches in to reset state between cases. Not part of the public API.
export const __test = {
  resetState(): void {
    memoryQueue.clear()
    processing = false
  },
  memorySize(): number {
    return memoryQueue.size
  },
  async clearIdb(): Promise<void> {
    const db = await openDb()
    if (!db) return
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        tx.objectStore(STORE_NAME).clear()
        tx.oncomplete = () => {
          db.close()
          resolve()
        }
        tx.onerror = () => {
          db.close()
          resolve()
        }
      } catch {
        db.close()
        resolve()
      }
    })
  },
  async idbCount(): Promise<number> {
    const items = await idbGetAll()
    return items.length
  },
}
